import { CoverContainer } from "./styles";
import {
  Avatar,
  Button,
  Container,
  Typography,
  IconButton,
} from "@mui/material";
import FmdGoodOutlinedIcon from "@mui/icons-material/FmdGoodOutlined";
import BusinessCenterIcon from "@mui/icons-material/BusinessCenter";
import LaunchIcon from "@mui/icons-material/Launch";
import { useTranslation } from "react-i18next";
import { useContext, useState } from "react";
import ApplyForJobModal from "./ApplyForJobModal";
import { useNavigate, useParams } from "react-router-dom";
import {
  useGetJobAdDetailsQuery,
  useTrackJobLinkClickMutation,
} from "../../../../../rq/hooks/useJob.mutation";
import CircularIndeterminate from "../../../../../components/CircularIndeterminate/CircularIndeterminate";
import { BusinessOutlined } from "@mui/icons-material";
import { AuthContext } from "../../../../../lib/context/AuthContext";
import toast from "react-hot-toast";
import ArrowBackIosRoundedIcon from "@mui/icons-material/ArrowBackIosRounded";

const JobOffer = () => {
  /**
   * hooks
   */
  const { t } = useTranslation();
  const token = localStorage.getItem("auth-token");
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  const [openWarningApplyJobModal, setOpenWarningApplyJobModal] =
    useState(false);
  const { mutate: trackClick } = useTrackJobLinkClickMutation();

  const { data: jobDetails, isLoading: jobDetailsLoading } =
    useGetJobAdDetailsQuery(Number(id), {
      onError: (err: any) => {
        if (err.response.data.errors[0].code === "job_is_blocked") {
          navigate(-1);
          toast.error(t("job_is_blocked"));
        }
      },
    });

  const isPremiumJob = Boolean(jobDetails?.isPremium);
  const showApplyCta =
    Boolean(user?.candidateId) &&
    Boolean(jobDetails?.canApply) &&
    !jobDetails?.isExpired;

  const handleWebsiteLinkClick = (url: string) => {
    if (id) {
      trackClick({
        jobPositionId: Number(id),
        userId: user?.userId || undefined,
        userAgent: navigator.userAgent,
      });
    }

    window.open(url, "_blank", "noopener,noreferrer");
  };

  const typeOfEmployments = [
    { id: 1, name: "cdd", label: t("cdd") },
    { id: 2, name: "internship", label: t("internship") },
    { id: 3, name: "jobStudent", label: t("job_student") },
  ];

  const descriptions = [
    {
      id: 1,
      name: t("enterprise_description"),
      data: jobDetails?.enterpriseDescription,
    },
    {
      id: 2,
      name: t("job_description_title"),
      data: jobDetails?.jobDescription,
    },
    {
      id: 3,
      name: t("function_title"),
      data: jobDetails?.functionDescription,
    },
    {
      id: 4,
      name: t("offer_title"),
      data: jobDetails?.offerDescription,
    },
    {
      id: 5,
      name: t("teaser_title"),
      data: jobDetails?.teaser,
    },
  ];

  if (jobDetailsLoading) return <CircularIndeterminate />;

  return (
    <>
      <div className="bg-[#3f6fe80d] pt-4">
        <Container sx={{ pt: "70px" }}>
          <div
            className=" top-3 flex items-center cursor-pointer"
            onClick={() => navigate(-1)}
          >
            <ArrowBackIosRoundedIcon
              className="fill-[#717275]"
              fontSize="small"
            />
            <Typography className="text-[#717275]">{t("return")}</Typography>
          </div>
        </Container>
        <CoverContainer>
          <div className="flex flex-col justify-center items-center gap-2">
            {/* {jobDetails?.isPremium && (
              <div className="bg-gradient-to-r from-amber-500 to-yellow-400 rounded-full px-3 py-1 font-montserrat text-xs font-semibold text-white shadow">
                {t("premium_job")}
              </div>
            )} */}
            {jobDetails?.isExpired && (
              <div className="bg-zinc-200 rounded-full px-2 py-1 font-montserrat text-sm font-medium text-zinc-700">
                {t("job_expired")}
              </div>
            )}
            {jobDetails?.businessLogoPath ? (
              <Avatar src={jobDetails?.businessLogoPath} variant="rounded" />
            ) : (
              <Avatar variant="rounded">
                <BusinessOutlined />
              </Avatar>
            )}
            <Typography className="font-bold text-3xl">
              {jobDetails?.title}
            </Typography>
            <Typography className="font-bold text-xl">
              {jobDetails?.businessName}
            </Typography>
            <div className="flex">
              <FmdGoodOutlinedIcon className="fill-[#3F6FE8]" />
              <Typography>{jobDetails?.businessAddress}</Typography>
            </div>
            {jobDetails?.companyWebsiteUrl && (
              <div className="flex items-center">
                <Button
                  onClick={() =>
                    handleWebsiteLinkClick(jobDetails.companyWebsiteUrl!)
                  }
                  startIcon={<LaunchIcon />}
                  variant="outlined"
                  size="small"
                  className="text-neutral-700 border-neutral-700 transition-none duration-0 hover:bg-[#28a745] outline-none hover:border-[#28a745] hover:text-white"
                >
                  {t("visit_company_website")}
                </Button>
              </div>
            )}
            <div>
              <Typography className="text-[#4A4D4B] text-sm">
                {jobDetails?.employmentTypeIds
                  ?.map((type) => {
                    const typeOfEmployment = typeOfEmployments.find(
                      (item) => item.id === type
                    );
                    return typeOfEmployment?.label;
                  })
                  .join(", ")}
              </Typography>
            </div>
            {showApplyCta && (
              <Button
                startIcon={<BusinessCenterIcon />}
                variant="contained"
                className="capitalize bg-[#3F6FE8]"
                onClick={() => setOpenWarningApplyJobModal(true)}
              >
                {t("apply_for_this_job")}
              </Button>
            )}
          </div>
        </CoverContainer>
      </div>

      <Container className="flex flex-col gap-6 py-6">
        {descriptions.map(({ data, name }) => (
          <div className="flex flex-col gap-2">
            {data && data !== "undefined" && (
              <>
                <Typography className="font-bold text-base">{name}</Typography>
                <Typography className="text-sm">
                  <div
                    data-job-content
                    dangerouslySetInnerHTML={{
                      __html: decodeURIComponent(data),
                    }}
                  />
                </Typography>
              </>
            )}
          </div>
        ))}
        {showApplyCta && (
          <Button
            startIcon={<BusinessCenterIcon />}
            variant="outlined"
            className="capitalize w-fit"
            onClick={() => setOpenWarningApplyJobModal(true)}
          >
            {t("apply_for_this_job")}
          </Button>
        )}
        {!token && !jobDetails?.isExpired && (
          <Button
            startIcon={<BusinessCenterIcon />}
            variant="outlined"
            className="capitalize w-fit"
            onClick={() => {
              toast.error(t("please_login_to_apply"));
              navigate("/login");
            }}
          >
            {t("apply_for_this_job")}
          </Button>
        )}
      </Container>
      <ApplyForJobModal
        open={openWarningApplyJobModal}
        handleClose={() => setOpenWarningApplyJobModal(false)}
      />
    </>
  );
};

export default JobOffer;
